import classNames from "./Login.module.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  GoogleAuthProvider,
  OAuthProvider,
  browserLocalPersistence,
  getAuth,
  signInWithPopup,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { LocalStorageKeys } from "../../../types";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { useLogEvent } from "../../../hooks/log";
import { useAxios } from "../../../hooks/axios";
import api from "../../../hooks/api-new";
import Loader from "../../common/Loader";
import Button from "../../common/Button";
import Header from "../../Header";
import { useNavigate } from "react-router-dom";
import googleImg from "../../../assets/login/google.svg";
import microsoftImg from "../../../assets/login/microsoft.svg";

// Stock images for login carousel
import conferenceStockImg from "../../../assets/stock/conference.webp";
import eventStockImg from "../../../assets/stock/event.webp";
import integrationStockImg from "../../../assets/stock/integration.webp";
import meetingStockImg from "../../../assets/stock/meeting.webp";
import phoneStockImg from "../../../assets/stock/phone.webp";

// Customer images
import timImg from "../../../assets/testimonies/tim.webp";
import yuvalImg from "../../../assets/testimonies/yuval.webp";
import vitzImg from "../../../assets/testimonies/vitz.webp";
import elraldoImg from "../../../assets/testimonies/elraldo.webp";
import maurizioImg from "../../../assets/testimonies/maurizio.webp";
import federicoImg from "../../../assets/testimonies/federico.webp";
import vincentImg from "../../../assets/testimonies/vincent.webp";
import mikeImg from "../../../assets/testimonies/mike.webp";
import mattImg from "../../../assets/testimonies/matt.webp";
import { IoPerson } from "react-icons/io5";

import { useRedirectAfterLogin } from "../../../hooks/redirect-after-login";
import BackToHome from "../../common/BackToHome";

const ImageCarousel = () => {
  const images = useMemo(
    () => [
      meetingStockImg,
      conferenceStockImg,
      eventStockImg,
      integrationStockImg,
      phoneStockImg,
    ],
    []
  );
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(true);
      setTimeout(() => {
        setIndex((prevIndex) => (prevIndex + 1) % images.length);
        setFade(false);
      }, 1000); // Needs to match css transition length
    }, 4 * 1000);
    return () => clearInterval(interval);
  });

  const current = useMemo(() => {
    return images[index];
  }, [images, index]);

  return (
    <div
      className={`${classNames.Carousel} ${classNames.ImageCarousel} ${
        fade ? classNames.FadeOut : ""
      }`}
    >
      <img src={current} alt="Image carousel" />
    </div>
  );
};

const CustomerTestimonyCarousel = () => {
  const testimonies = useMemo(
    () => [
      {
        name: "Vitz",
        title: "Co-Founder, Partner & Managing Director at ForgeWorks",
        testimony:
          "What impressed me was Langfinity's ability to pick up cultural and linguistic nuances, even in complex languages like Catalan or Macedonian. It provides the right tone and formality depending on the context, which is critical in professional conversations. Unlike previous tools, Langfinity allowed us to hold genuine two-way conversations in real time, where we could ask immediate follow-up questions and probe deeper into issues. This made a huge difference in how we engage with our clients.",
        image: vitzImg,
      },

      {
        name: "Yuval",
        title: "AI Integration Manager at Filuet",
        testimony:
          "Meetings that used to take twice as long because of manual translations are now much shorter. It saves us so much time and helps us collaborate much faster and more effectively. Now, our country managers can freely share their experiences and updates without struggling with language differences. The meetings flow much better, and everyone feels included.",
        image: yuvalImg,
      },

      {
        name: "Eraldo",
        title: "Sales Manager at LinkedIn",
        testimony:
          "I never imagined that I could speak Mandarin perfectly with people and through Langfinity, I feel that I can talk to the whole world. It is magical. This is to give people the opportunity to communicate with the world without feeling embarrassed due to language barriers. Without fear, you will be able to fully express yourself in your native language and get your messages across. I'm very satisfied with Langfinity.",
        image: elraldoImg,
      },
      {
        name: "Maurizio",
        title:
          "Customer Obsession Circle Leader at The European House-Ambrosetti",
        testimony:
          "Before Langfinity, our search for interpreters was a time-consuming, expensive affair. Now, not only do we save on time and costs, but we also provide an inclusive experience for diverse voices to interact seamlessly. Langfinity's AI-powered translation technology enhances participant satisfaction, solidifying our stance as pioneers in embracing AI neural machine translation for comprehensive language inclusivity as an organization.",
        image: maurizioImg,
      },
      {
        name: "Federico",
        title: "PMO of Netval",
        testimony:
          "We really enjoyed the experience. The translation is 95% precise between Italian and English. The quality is impressive - fast and accurate.",
        image: federicoImg,
      },
      {
        name: "Vincent",
        title: "Business Developer of Netval",
        testimony:
          "We are an innovation and technology-driven organization. Leveraging language AI to solve real pain points in language accessibility aligns with our mission and values.",
        image: vincentImg,
      },
      {
        name: "Mike",
        title: "Project Manager at the New Apostolic Church Canada",
        testimony:
          "Langfinity was transformational for us. It made the impossible possible—allowing everyone at NAC to come together, communicate, and connect in their native languages. The translation was instant, accurate, and so natural that it felt like magic. It's not just a tool; it's a bridge that brought our community closer together, regardless of language.",
        image: mikeImg,
      },
      {
        name: "Matt",
        title: "Language Coordinator at the New Apostolic Church Canada",
        testimony:
          "Setting it up was a breeze. We went from no translation to real-time translation in four languages in just 5 minutes. The user experience was so intuitive—attendees just scanned a QR code, and they were instantly connected to the translation they needed.",
        image: mattImg,
      },
      {
        name: "Tim",
        title: "Managing Director at iCustomer",
        testimony:
          "With Langfinity, we didn't need to bend over backward to hire local talent before entering new markets. It gave us the confidence to scale into South America and beyond while saving significantly on operational costs.",
        image: timImg,
      },
    ],
    []
  );

  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(true);
      setTimeout(() => {
        setIndex((prevIndex) => (prevIndex + 1) % testimonies.length);
        setFade(false);
      }, 1000); // Needs to match css transition length
    }, 10 * 1000);

    return () => clearInterval(interval);
  }, [testimonies.length]);

  const current = useMemo(() => {
    return testimonies[index];
  }, [testimonies, index]);

  return (
    <div className={classNames.Testimonies}>
      <h2>What our customers say</h2>
      <h1>“</h1>

      <div
        className={`${classNames.Carousel} ${fade ? classNames.FadeOut : ""}`}
      >
        <h3>{current.testimony}</h3>
        <div className={classNames.Customer}>
          {current.image ? (
            <img src={current.image} alt={current.name} />
          ) : (
            <IoPerson />
          )}
          <div>
            <h3>{current.name}</h3>
            <h3>{current.title}</h3>
          </div>
        </div>
      </div>
    </div>
  );
};

function Login(props: { signup?: boolean }) {
  const logEvent = useLogEvent();

  const axios = useAxios();
  const login = api.useLogin(axios);
  const navigate = useNavigate();
  const [redirectAfterLogin] = useRedirectAfterLogin();

  const signInWithFirebaseProvider = useCallback(
    async (providerId: "google" | "microsoft") => {
      logEvent({
        type: "attmept_login_with_firebase",
        details: { provider: providerId },
      });

      const auth = getAuth();
      const IGNORED_FIREBASE_ERROR_CODES = [
        "auth/popup-closed-by-user",
        "auth/cancelled-popup-request",
        "auth/popup-blocked",
        "auth/user-cancelled",
      ];

      try {
        auth.useDeviceLanguage();
        await auth.setPersistence(browserLocalPersistence);

        let provider;
        switch (providerId) {
          case "microsoft":
            provider = new OAuthProvider("microsoft.com");
            break;
          case "google":
            provider = new GoogleAuthProvider();
            provider.setCustomParameters({ prompt: "select_account" });
            break;
        }

        await signInWithPopup(auth, provider);
        await login();

        logEvent({
          type: "succeed_login_with_firebase",
          details: { provider: providerId },
        });

        // Set local storage to expect login flag when user comes back
        localStorage.setItem(LocalStorageKeys.EXPECT_LOGIN, "true");

        if (redirectAfterLogin.pathname) {
          navigate(redirectAfterLogin.pathname, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } catch (error) {
        if (error instanceof FirebaseError) {
          if (IGNORED_FIREBASE_ERROR_CODES.includes(error.code)) {
            console.warn("Ignored firebase error", error);
          } else if (
            error.code === "auth/account-exists-with-different-credential"
          ) {
            const oppositeProvider =
              providerId === "google" ? "Microsoft" : "Google";
            toast(
              <div className="Toast ErrorToast">
                <div>{`You already have a Langfinity account under ${oppositeProvider}, please sign in with ${oppositeProvider} instead`}</div>
              </div>
            );
          } else {
            toast(
              <div className="Toast ErrorToast">
                {"Something went wrong, please try again or contact support"}
              </div>,
              {
                type: "error",
              }
            );
            console.error(error);
            Sentry.captureException(error);
          }

          logEvent({
            type: "fail_login_with_firebase",
            details: { provider: providerId, error: error },
          });
        }
      }
    },
    [logEvent, login, navigate, redirectAfterLogin]
  );

  const loginWithGoogle = useCallback(async () => {
    logEvent({ type: "click_continue_with_google" });
    await signInWithFirebaseProvider("google");
  }, [signInWithFirebaseProvider, logEvent]);

  const loginWithMicrosoft = useCallback(async () => {
    logEvent({ type: "click_continue_with_microsoft" });
    await signInWithFirebaseProvider("microsoft");
  }, [signInWithFirebaseProvider, logEvent]);

  // Email login state
  const [email, setEmail] = useState("");
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [loading, setLoading] = useState(false);

  const sendVerificationCode = api.useSendVerificationCode(axios);

  const signInWithEmail = useCallback(async () => {
    logEvent({ type: "click_continue_with_email" });
    logEvent({
      type: "send_email_verification_code",
      details: { email: email },
    });

    setLoading(true);
    try {
      // Send verification code to email from api
      await sendVerificationCode({
        email,
      });
      toast(
        <div className="Toast SuccessToast">
          {"The code has been successfully sent to your email"}
        </div>,
        {
          type: "success",
        }
      );
      // Render verification code input on success
      setShowVerifyEmail(true);
      setShowResendCode(false);
    } catch (error) {
      toast(
        <div className="Toast ErrorToast">
          {"Something went wrong, please try again or contact support"}
        </div>,
        {
          type: "error",
        }
      );
      console.error(error);
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  }, [email, sendVerificationCode, logEvent]);

  const [verificationCode, setVerificationCode] = useState("");
  const [verifyCodeDisabled, setVerifyCodeDisabled] = useState(true);

  const [showResendCode, setShowResendCode] = useState(false);
  useEffect(() => {
    if (showResendCode || !showVerifyEmail) {
      return;
    }

    const resendCodeTimeout = setTimeout(() => {
      setShowResendCode(true);
    }, 45 * 1000);

    return () => {
      clearTimeout(resendCodeTimeout);
    };
  }, [showResendCode, showVerifyEmail]);

  // Check we can verify code
  useEffect(() => {
    const enabled = verificationCode.length >= 6;
    setVerifyCodeDisabled(!enabled);
    return () => {};
  }, [verificationCode]);

  const verifyCode = api.useVerifyCode(axios);
  const onClickVerifyCode = useCallback(async () => {
    try {
      logEvent({
        type: "attempt_login_with_email",
        details: { email: email },
      });
      setLoading(true);
      // Verify code with api
      const verifyCodeResponse = await verifyCode({
        email: email,
        code: verificationCode,
      });

      localStorage.setItem(
        LocalStorageKeys.LANGFINITY_TOKEN,
        verifyCodeResponse.data.encodedToken
      );
      // Force storage event on same tab
      window.dispatchEvent(new Event("storage"));

      // Small hack to make sure local storage sets token
      await new Promise((r) => setTimeout(r, 100));

      await login();

      logEvent({
        type: "succeed_login_with_email",
        details: { email: email },
      });

      localStorage.setItem(LocalStorageKeys.EXPECT_LOGIN, "true");

      if (redirectAfterLogin.pathname) {
        navigate(redirectAfterLogin.pathname, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (error) {
      toast(
        <div className="Toast ErrorToast">
          {"Something went wrong, please try again"}
        </div>,
        {
          type: "error",
        }
      );
      console.error(error);
      Sentry.captureException(error);
      logEvent({
        type: "fail_login_with_email",
        details: { email: email, error: error },
      });
    } finally {
      setLoading(false);
    }
  }, [
    email,
    login,
    verificationCode,
    verifyCode,
    navigate,
    redirectAfterLogin,
    logEvent,
  ]);

  return (
    <div className={classNames.Login}>
      <Header />
      <BackToHome />
      <div className={classNames.LoginContent}>
        <div>
          <h1>{props.signup ? "Sign up" : "Log in"}</h1>

          <div className={classNames.LoginButtons}>
            <Button
              onClick={loginWithGoogle}
              color="gray"
              borderColor="dark-gray"
              backgroundColor="black"
            >
              <img src={googleImg} alt="Google" />
              <span>Continue with Google</span>
            </Button>
            <Button
              onClick={loginWithMicrosoft}
              color="gray"
              borderColor="dark-gray"
              backgroundColor="black"
            >
              <img src={microsoftImg} alt="Microsoft" />
              <span>Continue with Outlook</span>
            </Button>
          </div>

          <div className={classNames.OrLine}>
            <div />
            Or
            <div />
          </div>
          {showVerifyEmail && (
            <h3 className={classNames.SentEmailMessage}>
              <div>We've sent an email with your code to</div>
              <div>{email}</div>
            </h3>
          )}
          <div className={classNames.LoginEmail}>
            {!showVerifyEmail ? (
              <>
                <h3>Email Address</h3>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  id="email"
                  type="email"
                  autoComplete="on"
                  placeholder="hal@email.com"
                />
              </>
            ) : (
              <>
                <h3>Enter your code</h3>
                <input
                  type="text"
                  placeholder="Verification Code"
                  value={verificationCode}
                  maxLength={6}
                  onChange={(e) => setVerificationCode(e.currentTarget.value)}
                />
              </>
            )}
          </div>
          <div className={classNames.Buttons}>
            <Button
              borderColor="dark-gray"
              color="gray"
              backgroundColor="black"
              onClick={() => {
                if (showVerifyEmail) {
                  setShowVerifyEmail(false);
                  return;
                }
                navigate(-1);
              }}
            >
              {showVerifyEmail ? "Back" : "Cancel"}
            </Button>
            {!showVerifyEmail ? (
              <Button disabled={!email.length} onClick={signInWithEmail}>
                {!loading ? "Continue" : <Loader inline={true} size={6} />}
              </Button>
            ) : (
              <Button disabled={verifyCodeDisabled} onClick={onClickVerifyCode}>
                {!loading ? "Continue" : <Loader inline={true} size={6} />}
              </Button>
            )}
          </div>
          {showResendCode && showVerifyEmail && (
            <h3
              className={classNames.ResendCode}
              onClick={() => setShowResendCode(false)}
            >
              Didn't receive email?{" "}
              <span onClick={signInWithEmail}>Resend</span>
            </h3>
          )}
        </div>

        {props.signup ? <CustomerTestimonyCarousel /> : <ImageCarousel />}
      </div>
    </div>
  );
}

export default Login;
